import React, { useState } from 'react';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import {showToast as toast} from '../toastManager';
import axios from 'axios';
import scalex_ai_logo from "../images/scalex_ai_logo.png"
import Cookies from 'js-cookie';
import { applyLoginPayloadToCookies } from '../authCookies';

function Login({ app_url }) {
    const [login_loading_btn, setLoginLoading] = useState(false);
    const [otp_loading_btn, setOTPLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const googleInitialized = React.useRef(false);
    const googleBtnRef = React.useRef(null);
    const [show_opt_div, setShowOTPDiv] = useState(false);
    const [email, setEmail] = useState('');
    const [otp_value, setOTPValue] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();


    const [errors, setErrors] = useState({});

    const loadGoogleScript = () => {
        if (window.google?.accounts?.id) return Promise.resolve(true);

        const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existing) {
            return new Promise((resolve, reject) => {
                existing.addEventListener('load', () => resolve(true), { once: true });
                existing.addEventListener('error', () => reject(new Error('Failed to load Google script')), { once: true });
            });
        }

        return new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = 'https://accounts.google.com/gsi/client';
            s.async = true;
            s.defer = true;
            s.onload = () => resolve(true);
            s.onerror = () => reject(new Error('Failed to load Google script'));
            document.body.appendChild(s);
        });
    };

    const applyLoginCookiesAndRedirect = (data) => {
        applyLoginPayloadToCookies(data, navigate);
    };

    const handleGoogleLogin = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        // if (googleLoading) return;
        setGoogleLoading(true);
        try {
            const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
            if (!clientId) {
                toast.error("Google Sign-In is not configured (missing REACT_APP_GOOGLE_CLIENT_ID).");
                setGoogleLoading(false);
                return;
            }

            await loadGoogleScript();

            // Initialize once — use renderButton (popup-based) instead of prompt() to avoid FedCM issues
            if (!googleInitialized.current) {
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: async (resp) => {
                        try {
                            const r = await axios.post(`${app_url}/login/google`, { credential: resp.credential }, { withCredentials: true });
                            if (r.data?.success) {
                                applyLoginCookiesAndRedirect(r.data);
                            } else {
                                toast.error(r.data?.message || "Google login failed");
                            }
                        } catch (err) {
                            toast.error(err?.response?.data?.message || "Google login failed");
                        } finally {
                            setGoogleLoading(false);
                        }
                    }
                });
                if (googleBtnRef.current) {
                    window.google.accounts.id.renderButton(googleBtnRef.current, {
                        type: 'standard',
                        theme: 'outline',
                        size: 'large',
                    });
                }
                googleInitialized.current = true;
            }

            // Trigger the rendered Google button popup (works for all accounts, no FedCM)
            const innerBtn = googleBtnRef.current?.querySelector('div[role="button"]');
            if (innerBtn) {
                innerBtn.click();
            } else {
                toast.error("Google sign-in could not be initialized. Please refresh and try again.");
                setGoogleLoading(false);
            }
        } catch (err) {
            toast.error(err?.message || "Google login failed");
            setGoogleLoading(false);
        }
    };


    // Handle Send Passcode
    const handleSendOTP = (e) => {
        e.preventDefault();

        setLoginLoading(true);

        if(validateForm()){
            try {
                axios.post(app_url+"/send_otp",{email:email, password:password}, {withCredentials:true})
                .then(res => res.data)
                .then(data => {
                    setLoginLoading(false);
                    setShowOTPDiv(true);
                })
                .catch(err => {
                    if(err && err.response && err.response.data && err.response.data.message){
                        toast.error(err.response.data.message);
                    }
                    else{
                        toast.error("Login Failed.");
                    }
                    console.log(err);
                    setLoginLoading(false);
                })
            } catch (error) {
                toast.error("Please try after some time.");
                setLoginLoading(false);
            }
        }
        else{
            setLoginLoading(false);
        }
    }


    // Per-field validators — shared by the on-blur handlers and validateForm()
    // so the visible error message is identical whether the user leaves the
    // field or clicks the button. Each returns the message string, or undefined
    // when the field is valid.
    const computeEmailError = (value) => {
        if ((value === null) || (value === undefined) || (`${value}`.trim() === '')) {
            return 'Email is required.';
        }
        if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(`${value}`.trim())) {
            return 'Please enter a valid email address.';
        }
        return undefined;
    };

    const computeOtpError = (value) => {
        const otp = `${value ?? ''}`.trim();
        if (!otp) {
            return 'Please enter Passcode.';
        }
        if (Number(otp) < 0) {
            return 'Passcode should not be negative.';
        }
        if (!/^\d{6}$/.test(otp)) {
            return 'Passcode must be 6 digits.';
        }
        return undefined;
    };

    // Validate inputs and set custom error messages
    const validateForm = () => {
        const newErrors = {};

        const emailError = computeEmailError(email);
        if (emailError) {
            newErrors.email = emailError;
        }

        if ((password === null) || (password === undefined) ||  (password.trim() === '')) {
            newErrors.password = 'Password is required.';
        }

        if(show_opt_div){
            const otpError = computeOtpError(otp_value);
            if (otpError) {
                newErrors.otp = otpError;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

  const handleSubmit = (e) => {
    e.preventDefault();
    setOTPLoading(true);
    if(validateForm()){
        try {
            axios.post(app_url+"/login",{email:email, password:password, otp:otp_value}, {withCredentials:true})
            .then(res => res.data)
            .then(data => {
                setLoginLoading(false);
                if(data["success"] === true){
                    console.log(data)
                    applyLoginCookiesAndRedirect(data);
                }
                else{
                    toast.error(data["message"]);
                }
                setOTPLoading(false)
            })
            .catch(err => {
                if(err && err.response && err.response.data && err.response.data.message){
                    toast.error(err.response.data.message);
                }
                else{
                    toast.error("Login Failed.");
                }
                console.log(err);
                setOTPLoading(false)
            })
        } catch (error) {
            toast.error("Please try after some time.");
            setOTPLoading(false)
        }
    }
    else{
        setOTPLoading(false)
    }
  };

  return (
    <>
        <Toaster />
        <div style={{ display: 'flex', minHeight: '100vh' }}>
            {/* Left Panel */}
            <div style={{
                flex: '0 0 50%',
                backgroundColor: '#132848',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* <svg width="52" height="52" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.9181 9.03296L14.7636 11.8806M14.7636 11.8806L29.8633 21.1081M14.7636 11.8806L27.0973 9.03296L29.8633 21.1081M14.7636 11.8806V30.0002M14.7636 11.8806L6.77393 20.2693L14.7636 30.0002M29.8633 21.1081L35.0643 20.2693M29.8633 21.1081L14.7636 30.0002M29.8633 21.1081L27.0973 32.1791L14.7636 30.0002M14.7636 30.0002L12.9181 32.8523" stroke="white" strokeWidth="1.69808"/>
                        <path d="M27.3135 8.29862L34.274 20.3545L27.3134 32.4098L13.3946 32.4092L6.43418 20.3533L13.393 8.29895L27.3135 8.29862Z" stroke="white" strokeWidth="1.69808"/>
                    </svg> */}
                    <img width="52" height="52" src={scalex_ai_logo} alt="ScaleX AI Logo" />
                    <span style={{ fontSize: '2rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>CognitiveUX.ai</span>
                </div>
            </div>

            {/* Right Panel */}
            <div style={{
                flex: '0 0 50%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                backgroundColor: '#fff',
                overflowY: 'auto',
            }}>
                <div style={{ width: '100%', maxWidth: '400px' }}>
                    {/* Title */}
                    <h1 style={{
                        fontSize: '1.75rem',
                        fontWeight: '700',
                        color: '#101828',
                        marginBottom: '8px',
                        marginTop: '0'
                    }}>Log in to your account</h1>
                    <p style={{
                        fontSize: '14px',
                        color: '#667085',
                        marginBottom: '28px',
                        marginTop: '0'
                    }}>Welcome back! Please enter your details.</p>

                    <Form onSubmit={e => { e.preventDefault(); }} noValidate>
                        {show_opt_div ? (
                            <>
                                <Form.Group className='mb-4' controlId="formBasicOTP">
                                    <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#344054', marginBottom: '6px', display: 'block' }}>Passcode</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Enter Passcode"
                                        min={100000}
                                        max={1000000}
                                        value={otp_value}
                                        onChange={(e) => setOTPValue(e.target.value.trim())}
                                        onBlur={() => setErrors((prev) => ({ ...prev, otp: computeOtpError(otp_value) }))}
                                        isInvalid={!!errors.otp}
                                        style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #D0D5DD', fontSize: '14px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.otp}</Form.Control.Feedback>
                                </Form.Group>

                                {otp_loading_btn ? (
                                    <Button variant="primary" type="button" disabled className="w-100"
                                        style={{ backgroundColor: '#132848', borderColor: '#132848', padding: '10px', borderRadius: '8px', fontSize: '1rem', fontWeight: '600' }}>
                                        <Spinner size='sm' />
                                    </Button>
                                ) : (
                                    <Button variant="primary" type="submit" onClick={handleSubmit} className="w-100"
                                        style={{ backgroundColor: '#132848', borderColor: '#132848', padding: '10px', borderRadius: '8px', fontSize: '1rem', fontWeight: '600' }}>
                                        Verify Passcode
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Sign in with Google */}
                                <Button variant="outline-secondary" type="button" className="w-100 mb-4" onClick={handleGoogleLogin}
                                    style={{ backgroundColor: 'white', color: '#344054', borderColor: '#D0D5DD', padding: '10px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)', opacity: 1 }}>
                                    {/* {googleLoading
                                        ? <Spinner size="sm" style={{ borderColor: '#344054', borderRightColor: 'transparent' }} />
                                        : <img width="20" height="20" src="https://developers.google.com/identity/images/g-logo.png" alt="Google" />
                                    }
                                    {googleLoading ? 'Signing in...' : 'Sign in with Google'} */}
                                    <img width="20" height="20" src="https://developers.google.com/identity/images/g-logo.png" alt="Google" />
                                    Sign in with Google
                                </Button>

                                {/* Divider */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                                    <div style={{ flex: 1, height: '1px', backgroundColor: '#EAECF0' }} />
                                    <span style={{ fontSize: '14px', color: '#667085', whiteSpace: 'nowrap' }}>or</span>
                                    <div style={{ flex: 1, height: '1px', backgroundColor: '#EAECF0' }} />
                                </div>

                                <Form.Group className='mb-4' controlId="formBasicEmail">
                                    <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#344054', marginBottom: '6px', display: 'block' }}>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value.trim())}
                                        onBlur={() => setErrors((prev) => ({ ...prev, email: computeEmailError(email) }))}
                                        isInvalid={!!errors.email}
                                        style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #D0D5DD', fontSize: '14px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className='mb-2' controlId="formBasicPassword">
                                    <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#344054', marginBottom: '6px', display: 'block' }}>Password</Form.Label>
                                    <div style={{ position: 'relative' }}>
                                        <Form.Control
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value.trim())}
                                            isInvalid={!!errors.password}
                                            style={{ padding: '10px 40px 10px 14px', borderRadius: '8px', border: '1px solid #D0D5DD', fontSize: '14px', boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}
                                        />
                                        <button type="button" onClick={() => setShowPassword(p => !p)}
                                            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#667085', lineHeight: 0 }}>
                                            {errors.password && errors.password.length > 0 ? null : showPassword ? (
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                                </svg>
                                            ) : (
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                    <circle cx="12" cy="12" r="3"/>
                                                </svg>
                                            )}
                                        </button>
                                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                                    </div>
                                </Form.Group>

                                {/* Remember me */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', marginTop: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="checkbox" id="rememberMe" style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#132848' }} />
                                        <label htmlFor="rememberMe" style={{ fontSize: '14px', color: '#344054', cursor: 'pointer', margin: 0, fontWeight: '500' }}>
                                            Remember for 30 days
                                        </label>
                                    </div>
                                </div>

                                {/* Sign in Button */}
                                {login_loading_btn ? (
                                    <Button variant="primary" type="button" disabled className="w-100 mb-3"
                                        style={{ backgroundColor: '#132848', borderColor: '#132848', padding: '10px', borderRadius: '8px', fontSize: '1rem', fontWeight: '600' }}>
                                        <Spinner size='sm' />
                                    </Button>
                                ) : (
                                    <Button variant="primary" type="button" onClick={handleSendOTP} className="w-100 mb-3"
                                        style={{ backgroundColor: '#132848', borderColor: '#132848', padding: '10px', borderRadius: '8px', fontSize: '1rem', fontWeight: '600' }}>
                                        Log in
                                    </Button>
                                )}

                                {/* Sign Up Link */}
                                <div style={{ textAlign: 'center', fontSize: '14px', color: '#667085' }}>
                                    Don't have an account?{' '}
                                    <Link to="/signup" style={{ color: '#132848', textDecoration: 'none', fontWeight: '600' }}>Sign up</Link>
                                </div>
                            </>
                        )}
                    </Form>
                </div>
            </div>
        </div>
        {/* Hidden container for Google's rendered button — needed for popup-based sign-in */}
        <div ref={googleBtnRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 }} aria-hidden="true" />
    </>
  );
}

export default Login;
